/**
 * useDevis Hook
 * React Query hooks for devis (quote) operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDevisList,
  getDevisById,
  createDevis,
  updateDevis,
  deleteDevis,
  addDevisLine,
  updateDevisLine,
  removeDevisLine,
  sendDevis,
  convertDevisToAffaire,
  searchContacts,
  peekNextDevisNumber
} from '../api/facturation.api';
import type { DevisFilters, CreateDevisData, AddDevisLineData } from '../types/facturation.types';

// Retry configuration for mutations with exponential backoff
const MUTATION_RETRY_CONFIG = {
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s... max 30s
};

// Query keys
export const devisKeys = {
  all: ['devis'] as const,
  lists: () => [...devisKeys.all, 'list'] as const,
  list: (filters: DevisFilters) => [...devisKeys.lists(), filters] as const,
  details: () => [...devisKeys.all, 'detail'] as const,
  detail: (id: number) => [...devisKeys.details(), id] as const,
  nextNumber: (year?: number) => [...devisKeys.all, 'next-number', year] as const,
  contacts: (query: string) => [...devisKeys.all, 'contacts', query] as const,
};

// Get paginated devis list
export const useDevisList = (filters: DevisFilters = {}) => {
  return useQuery({
    queryKey: devisKeys.list(filters),
    queryFn: () => getDevisList(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get single devis by ID
export const useDevis = (id: number) => {
  return useQuery({
    queryKey: devisKeys.detail(id),
    queryFn: () => getDevisById(id),
    enabled: !!id,
  });
};

// Peek next devis number
export const useNextDevisNumber = (year?: number) => {
  return useQuery({
    queryKey: devisKeys.nextNumber(year),
    queryFn: () => peekNextDevisNumber(year),
  });
};

// Search contacts
export const useContactSearch = (query: string) => {
  return useQuery({
    queryKey: devisKeys.contacts(query),
    queryFn: () => searchContacts(query),
    enabled: query.length >= 2,
    staleTime: 60000, // 1 minute
  });
};

// Create devis mutation
export const useCreateDevis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDevisData) => createDevis(data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
      queryClient.invalidateQueries({ queryKey: devisKeys.nextNumber() });
    },
  });
};

// Update devis mutation
export const useUpdateDevis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateDevisData> }) =>
      updateDevis(id, data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Delete devis mutation
export const useDeleteDevis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteDevis(id),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Add line mutation
export const useAddDevisLine = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ devisId, data }: { devisId: number; data: AddDevisLineData }) =>
      addDevisLine(devisId, data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Update line mutation
export const useUpdateDevisLine = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      devisId,
      lineId,
      data
    }: {
      devisId: number;
      lineId: number;
      data: Partial<AddDevisLineData>;
    }) => updateDevisLine(devisId, lineId, data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Remove line mutation
export const useRemoveDevisLine = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ devisId, lineId }: { devisId: number; lineId: number }) =>
      removeDevisLine(devisId, lineId),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Send devis mutation (DRAFT → SENT)
export const useSendDevis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => sendDevis(id),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};

// Convert devis to affaire products
export const useConvertDevis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ devisId, affaireId }: { devisId: number; affaireId: string }) =>
      convertDevisToAffaire(devisId, affaireId),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { devisId }) => {
      queryClient.invalidateQueries({ queryKey: devisKeys.detail(devisId) });
      queryClient.invalidateQueries({ queryKey: devisKeys.lists() });
    },
  });
};
